import {AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, signal, ViewChild, ViewEncapsulation} from '@angular/core';
import {TenantConfig} from '../../model/tenant-config';
import {AusschreibungService} from '../../service/ausschreibung.service';
import {TenantConfigService} from '../../service/tenant-config.service';

interface HeaderMenuItem {
  label: string;
  href: string;
}

@Component({
    selector: 'dah-stzh-header',
    templateUrl: './stzh-header.component.html',
    styleUrls: ['./stzh-header.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
})
export class StzhHeaderComponent implements AfterViewInit, OnDestroy {
  signedIn = signal(false);
  userName = signal('');
  navOpen = signal(false);
  accountOpen = signal(false);
  tenant: TenantConfig = this.tenantConfigService.current;

  @ViewChild('logobarEl') logobarEl?: ElementRef<HTMLElement>;
  @ViewChild('metabarEl') metabarEl?: ElementRef<HTMLElement>;

  /* Dynamisch sticky wie der Vendor-<stzh-header> (stzh-header.js:
     scrollListener()/updatePosition()): Header ist normalerweise im
     normalen Fluss, wird aber "fixed" sobald man weiter als seine eigene
     Höhe runtergescrollt hat, und zeigt sich dabei nur (transform statt
     Layout-Sprung) während man aktiv nach oben scrollt ("sticky"), sonst
     bleibt er ausserhalb des Viewports geparkt. logobarHeight/headerHeight
     werden wie im Original per ResizeObserver/offsetHeight gemessen statt
     hartkodiert, damit die drei Breakpoint-Stufen (66/88/135px Logobar)
     automatisch stimmen. */
  headerFixed = signal(false);
  headerSticky = signal(false);
  /* Transform-Transition erst einen Frame nach dem Fixieren aktivieren (Vendor:
     fixedTransition in fixedWatcher nach waitForNextRender) — sonst animiert
     der Sprung auf translateY(-100%) beim erstmaligen Fixieren sichtbar. */
  fixedTransition = signal(false);
  /* Vendor hideLogo: ab 900px Breite blendet das Logo schon ab 1px Scroll aus
     (stzh-header.js scrollListener: headerOverlap > 1 && isMedium), lange bevor
     der Header fixed wird. */
  logoHidden = signal(false);
  logobarHeight = signal(0);
  headerHeight = signal(0);
  private lastScrollY = 0;
  private resizeObserver?: ResizeObserver;
  private mediumQuery = window.matchMedia('(min-width: 900px)');
  /* Fokus-Rückgabe beim Schliessen des Offcanvas (Vendor: focus-trap mit
     returnFocusOnDeactivate) — merkt sich das Element, das das Menü geöffnet hat. */
  private navOpener?: HTMLElement;

  headerMenuItems: HeaderMenuItem[];

  constructor(
    private ausschreibungService: AusschreibungService,
    private tenantConfigService: TenantConfigService,
    private ngZone: NgZone,
    private elementRef: ElementRef<HTMLElement>,
  ) {
    this.ausschreibungService.isSignedIn().subscribe(signedIn => this.signedIn.set(signedIn));
    this.ausschreibungService.displayName$.subscribe(user => this.userName.set(user));
    this.headerMenuItems = [{
      label: 'Hilfe zur Angebotseingabe',
      href: this.addBaseUrl('ahb-hilfe-angebotseingabe'),
    }];
  }

  ngAfterViewInit(): void {
    this.lastScrollY = window.scrollY;
    this.measureHeader();
    this.resizeObserver = new ResizeObserver(() => this.measureHeader());
    this.resizeObserver.observe(this.logobarEl!.nativeElement);
    this.resizeObserver.observe(this.metabarEl!.nativeElement);
    // Scroll-Listener bewusst ausserhalb der Angular-Zone: zone.js würde sonst
    // jeden Scroll-Frame zu einer app-weiten Change Detection machen. Der Handler
    // schreibt nur Signals — die planen CD selbst ein (in zoned wie zoneless),
    // darum kein ngZone.run nötig; ein Signal-set auf denselben Wert ist per
    // Gleichheit ein No-op und löst keine CD aus.
    this.ngZone.runOutsideAngular(() => window.addEventListener('scroll', this.onScroll, {passive: true}));
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
  }

  addBaseUrl = (path: string) => this.ausschreibungService.addBaseUrl(path);

  setNavOpen(open: boolean): void {
    const host = this.elementRef.nativeElement;
    if (open) {
      // Scrollbar-Breite VOR dem Scroll-Lock messen — danach ist sie weg.
      document.documentElement.style.setProperty('--dah-scrollbar-width',
        `${window.innerWidth - document.documentElement.clientWidth}px`);
      this.navOpener = document.activeElement as HTMLElement;
    }
    this.navOpen.set(open);
    document.body.classList.toggle('dah-nav-open', open);
    // Vendor disableSiblings(): Seiteninhalt hinter dem Offcanvas für
    // Screenreader ausblenden, solange das Menü offen ist.
    Array.from(host.parentElement!.children).filter(el => el !== host)
      .forEach(el => open ? el.setAttribute('aria-hidden', 'true') : el.removeAttribute('aria-hidden'));
    if (open) {
      // Vendor handleBurgerClick: Fokus nach dem Rendern auf den ersten Menüpunkt.
      setTimeout(() => host.querySelector<HTMLElement>('.dah-offcanvas__panel a')?.focus(), 100);
    } else {
      this.navOpener?.focus();
      this.navOpener = undefined;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!(event.target as HTMLElement).closest('.dah-account')) {
      this.accountOpen.set(false);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.accountOpen()) {
        this.accountOpen.set(false);
        this.elementRef.nativeElement.querySelector<HTMLElement>('.dah-account__toggle')?.focus();
      } else if (this.navOpen()) {
        this.setNavOpen(false);
      }
    } else if (event.key === 'Tab' && this.navOpen()) {
      this.trapFocus(event);
    }
  }

  /* Vendor createFocusTrap(this.element): Tab zirkuliert bei offenem Offcanvas
     innerhalb der Komponente (Header inkl. Burger/Account + Panel-Links) —
     der restliche Seiteninhalt ist derweil aria-hidden. */
  private trapFocus(event: KeyboardEvent): void {
    const tabbables = Array.from(
      this.elementRef.nativeElement.querySelectorAll<HTMLElement>('a[href], button'),
    ).filter(el => el.checkVisibility());
    const first = tabbables[0];
    const last = tabbables[tabbables.length - 1];
    const active = document.activeElement as HTMLElement;
    if (event.shiftKey && (active === first || !tabbables.includes(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !tabbables.includes(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  private onScroll = (): void => {
    if (this.navOpen()) return;
    const scrollY = window.scrollY;
    const scrollingUp = this.lastScrollY > scrollY;
    this.lastScrollY = scrollY;
    const belowStayStickyPoint = scrollY > this.logobarHeight();
    const belowStartStickyPoint = scrollY > this.headerHeight();
    let sticky = this.headerSticky();
    if (scrollingUp && belowStayStickyPoint) {
      if (!sticky && belowStartStickyPoint) sticky = true;
    } else {
      sticky = false;
    }
    this.headerSticky.set(sticky);
    this.headerFixed.set(belowStartStickyPoint || (scrollingUp && belowStayStickyPoint && sticky));
    this.logoHidden.set(this.mediumQuery.matches && scrollY > 1);
    // Doppel-rAF: das erste rAF feuert noch vor dem Paint des Frames, in dem
    // is-fixed gesetzt wird — erst das zweite liegt sicher einen Paint später.
    requestAnimationFrame(() => requestAnimationFrame(() =>
      this.fixedTransition.set(this.headerFixed() || this.navOpen())));
  };

  private measureHeader(): void {
    const logobar = this.logobarEl!.nativeElement.offsetHeight;
    this.logobarHeight.set(logobar);
    this.headerHeight.set(logobar + this.metabarEl!.nativeElement.offsetHeight);
    // Wie Vendor handleResize → scrollListener: Fixed-/Sticky-/Logo-Zustand mit
    // den neuen Höhen nachführen (z.B. Breakpoint-Wechsel in gescrolltem Zustand).
    this.onScroll();
  }
}
